import { requestedPage } from './storePageRoutes';

// The first answers a store page needs, written into the page by the API that serves it, so the storefront draws the
// store on its first pass instead of waiting on four round trips to the same API. The storefront's API client answers
// a read from here while the page is young; later reads, and anything this list does not name, go to the network.

/** The global the answers are written under. */
export const BOOT_GLOBAL = '__XSHOP_BOOT__';

/** How long after the page opened the written answers stand in for the API. */
export const BOOT_FRESH_MS = 30_000;

/** The answers, each the API's whole envelope, keyed by the path the storefront asks it under. */
export type BootAnswers = Record<string, unknown>;

/** The global a drawn page names its answers' files under, when they are served as files rather than written in. */
export const BOOT_FILES_GLOBAL = '__XSHOP_BOOT_FILES__';

/** Where one answer is served as a file named by its content: the same answer is the same file on every page. */
export const BOOT_FILE_PREFIX = '/_boot/';

/** Each answer's file, keyed by the path the storefront asks it under. */
export type BootFiles = Record<string, string>;

/** Address flags that make a visit something other than a shopper's: a preview, the builder, a capture, a block picture. */
const NOT_A_SHOPPER = ['theme', 'editor', 'capture', 'tenant', 'blockPreview'];

// Reads the product an address names, as the storefront does: ?product=, else the /products/<handle> segment as written.
export const addressedProductSlug = (pathname: string, search: string): string | null =>
  new URLSearchParams(search).get('product') ?? pathname.match(/^\/products\/([^/]+)/)?.[1] ?? null;

// Lists the reads a store page makes before anything else — its theme page, the store, its products, the COD form and
// the product it sells — or none for a visit that is not a shopper's, which reads other things.
export function bootPaths(pathname: string, search: string): string[] {
  const params = new URLSearchParams(search);
  if (NOT_A_SHOPPER.some((flag) => params.has(flag))) return [];
  const product = addressedProductSlug(pathname, search);
  return [
    `/tenant/theme?page=${encodeURIComponent(requestedPage(pathname, search))}`,
    '/tenant/settings',
    '/products',
    '/cod-form',
    ...(product ? [`/products/by-slug/${product}`] : [])
  ];
}
